import LegalLayout, { LegalHero, LegalSection, LegalList } from "@/components/LegalLayout";
import { AlertTriangle } from "lucide-react";

export default function DisclaimerPage() {
  return (
    <>
      <LegalHero
        eyebrow="LEGAL · AI DISCLAIMER"
        title="AI Disclaimer"
        subtitle="A plain-English statement about what RADAR's AI outputs are — and aren't."
        updated="2026"
      />
      <LegalLayout>
        <div
          data-testid="ai-disclaimer-callout"
          className="mb-10 bg-[#FF9F0A]/10 border border-[#FF9F0A]/40 rounded-sm p-5 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-[#FF9F0A] shrink-0 mt-0.5"/>
          <div>
            <div className="kpi-label text-[#FF9F0A] mb-1">/ TL;DR</div>
            <p className="text-[14px] text-white leading-relaxed">
              RADAR is a decision-support tool, not a decision-maker. Always validate
              business-critical claims with primary sources before acting.
            </p>
          </div>
        </div>

        <LegalSection id="how-it-works" title="1. How RADAR Produces Outputs">
          <p>
            RADAR uses large language models (currently Anthropic's Claude Haiku 4.5) combined with
            real-time web search (Tavily) to synthesise a 21-section executive intelligence report
            for any company name you enter. Every factual claim in the report is grounded on the
            web sources fetched at generation time, listed in Section 21 of every report.
          </p>
        </LegalSection>

        <LegalSection id="limitations" title="2. Known Limitations">
          <p>AI outputs may occasionally contain:</p>
          <LegalList items={[
            "Inaccuracies in financial figures, organisational details, or recent events — especially for fast-moving private companies.",
            "Assumptions clearly flagged in Section 21 (Data Confidence) when the model could not corroborate a claim.",
            "Stale information when public web data is itself outdated.",
            "Hallucinations — the model generating plausible-sounding facts that do not exist. We mitigate this with Tavily grounding, but it cannot be eliminated entirely.",
            "Cultural, regional, or linguistic bias inherited from public-web training data.",
          ]} />
        </LegalSection>

        <LegalSection id="not-advice" title="3. Outputs Should NOT Be Treated As">
          <LegalList items={[
            "Legal advice — RADAR does not produce legal opinions, contracts, compliance guidance, or jurisdiction-specific interpretations.",
            "Financial advice — attractiveness scores, deal sizes, and revenue figures are estimates for prioritisation; never trading or investment recommendations.",
            "Investment recommendations — RADAR is a sales-intelligence tool, not a securities analyst.",
            "Hiring or personnel decisions — RADAR's mention of leadership names is for outreach context only.",
            "Guaranteed strategic outcomes — pipeline forecasts, win probabilities, and CXO email drafts are starting points, not predictions.",
            "Statements of fact about identifiable individuals, beyond publicly-stated job titles and roles.",
          ]} />
        </LegalSection>

        <LegalSection id="user-responsibility" title="4. User Responsibility">
          <p>
            Users are expected to independently validate critical insights before acting upon them.
            For high-stakes decisions (large deals, M&amp;A, partnerships, contractual commitments),
            cross-reference RADAR's claims against authoritative primary sources — annual reports,
            SEC/regulatory filings, official press releases, direct conversations with the account.
          </p>
          <p>
            RADAR provides a "Confidence Level" + "Assumptions Made" block on every report
            (Section 21) precisely so you know what to verify first.
          </p>
        </LegalSection>

        <LegalSection id="liability" title="5. No Warranty &amp; Limited Liability">
          <p>
            RADAR is provided "as is" and "as available". We make no warranty — express, implied,
            or statutory — regarding accuracy, completeness, merchantability, fitness for a
            particular purpose, or non-infringement of any RADAR-generated output. See our
            <a className="text-[#0A84FF] hover:underline mx-1" href="/terms">Terms of Service</a>
            for the full liability framework.
          </p>
        </LegalSection>

        <LegalSection id="report-issue" title="6. Reporting Inaccuracies">
          <p>
            Found a factual error in a report? Help us improve — email
            <a className="text-[#0A84FF] hover:underline mx-1" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a>
            with the report ID, the section, and a brief description. We use this feedback to
            calibrate prompts and grounding strategy.
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
}
